/**
 * i18n scaffold — Phase A (Foundation).
 *
 * • English is the ONLY shipped locale today but the infrastructure is ready
 *   to accept `es.json`, `pt.json`, etc. Drop a new JSON in `./locales/` and
 *   register it in `resources` below — no code changes needed.
 * • Language is auto-detected from browser (`navigator.language`), overridable
 *   from the Avatar dropdown ("Language" submenu). The choice persists in
 *   localStorage under key `i18nextLng`.
 * • Silent fallback: if a user's browser language isn't shipped yet, we quietly
 *   render English. No "coming soon" banners (per product direction 2026-02).
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import pt from './locales/pt.json';

// Only English is bundled today. When a new locale JSON is added, register it
// here alongside `en`.
export const SUPPORTED_LOCALES = [
  { code: 'en', label: 'English',    native: 'English' },
  { code: 'es', label: 'Spanish',    native: 'Español' },
  { code: 'fr', label: 'French',     native: 'Français' },
  { code: 'pt', label: 'Portuguese', native: 'Português' },
];

const resources = {
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  pt: { translation: pt },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LOCALES.map((l) => l.code),
    // Non-supported detected languages silently resolve to `en`.
    nonExplicitSupportedLngs: false,
    interpolation: { escapeValue: false }, // React already escapes.
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    returnEmptyString: false,
  });

export default i18n;
